import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import FeedbackModal from './FeedbackModal';

/**
 * The way in, from anywhere.
 *
 * It lives in the planner header rather than on each screen for one
 * reason: the moment worth capturing is the moment something annoys
 * someone, and that moment does not survive a hunt through the menu.
 *
 * It is the one coloured thing in a header of grey icons, because a
 * second outline glyph next to the help glyph is a button nobody finds.
 * The lightbulb reads as "I have an idea", which is a different errand
 * from the question mark beside it. On anything wider than a tablet it
 * carries the word too -- an icon alone has to be guessed at.
 *
 * Hover changes colour only. Growing the button on hover shifts the
 * toolbar beside it.
 *
 * It opens a menu with two errands: suggest something, or check on
 * what you already suggested. My Requests is not in the sidebar, so this
 * menu is the way to it.
 *
 * The modal is not mounted until it is chosen, so every planner page
 * pays a button for this and nothing else: no stylesheet, no state, no
 * request.
 */

const RED = '#CC0000';
const RED_TINT = '#FFF0F0';

const MENU_ID = 'feedback-menu';
const MY_REQUESTS = '/planner/feedback';

const itemSx = {
  py: 1.1,
  px: 1.75,
  gap: 0.25,
  fontFamily: "'Lato', sans-serif",
  '& .MuiListItemIcon-root': { minWidth: 32, color: RED },
  '& .MuiListItemText-primary': { fontSize: 14, fontWeight: 700, color: '#1A1A2E' },
  '& .MuiListItemText-secondary': { fontSize: 12.5 },
  '&:hover, &.Mui-focusVisible': { background: RED_TINT },
};

export default function FeedbackLauncher() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const history = useHistory();
  const roomForWords = useMediaQuery('(min-width:900px)');
  const menuOpen = Boolean(anchor);

  const closeMenu = () => setAnchor(null);
  const suggest = () => {
    closeMenu();
    setOpen(true);
  };
  const myRequests = () => {
    closeMenu();
    history.push(MY_REQUESTS);
  };

  return (
    <>
      <Tooltip title={menuOpen ? '' : 'Suggest an improvement or follow your requests'}>
        <Button
          type="button"
          aria-label="Feedback"
          aria-haspopup="menu"
          aria-controls={menuOpen ? MENU_ID : undefined}
          aria-expanded={menuOpen ? 'true' : undefined}
          onClick={(e) => setAnchor(e.currentTarget)}
          startIcon={<TipsAndUpdatesOutlinedIcon sx={{ fontSize: 20 }} />}
          endIcon={
            roomForWords ? (
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 18,
                  transition: 'transform .18s ease',
                  transform: menuOpen ? 'rotate(180deg)' : 'none',
                }}
              />
            ) : null
          }
          disableElevation
          sx={{
            ml: '10px',
            minWidth: 44,
            height: 44,
            px: roomForWords ? 1.75 : 1.25,
            borderRadius: '999px',
            border: `1px solid ${RED}26`,
            background: menuOpen ? RED : RED_TINT,
            color: menuOpen ? '#fff' : RED,
            fontFamily: "'Lato', sans-serif",
            fontWeight: 800,
            fontSize: 13.5,
            letterSpacing: '.01em',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            transition: 'background .18s ease, color .18s ease, border-color .18s ease',
            '& .MuiButton-startIcon': { mr: roomForWords ? 0.75 : 0, ml: 0 },
            '& .MuiButton-endIcon': { ml: 0.25, mr: -0.5 },
            '&:hover': { background: RED, color: '#fff', borderColor: RED },
            '&:focus-visible': { outline: `3px solid ${RED}59`, outlineOffset: 2 },
          }}
        >
          {roomForWords ? 'Feedback' : null}
        </Button>
      </Tooltip>
      <Menu
        id={MENU_ID}
        anchorEl={anchor}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 260,
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(26,26,46,.14)',
          },
        }}
      >
        <MenuItem onClick={suggest} sx={itemSx}>
          <ListItemIcon>
            <TipsAndUpdatesOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Suggest an improvement"
            secondary="Report a bug or share an idea"
          />
        </MenuItem>
        <MenuItem onClick={myRequests} sx={itemSx}>
          <ListItemIcon>
            <ChatBubbleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="My requests" secondary="Status and replies on what you sent" />
        </MenuItem>
      </Menu>
      {open ? <FeedbackModal open onClose={() => setOpen(false)} /> : null}
    </>
  );
}
